<template>
  <page-content
    :menu-title="$t('pageTitle.user.chats')"
    bg-shade
    no-top-content
    :sidebar-width="sidebarWidth"
  >
    <template
      v-if="!isShowChatFilterInDropdown"
      v-slot:menu
    >
      <chats-menu />
    </template>
    <template v-slot:after-sidebar-title>
      <tm-sidebar-header-wrapper>
        <tm-sidebar-header-button
          v-tooltip="{ text: $t('pageTabTitle.user.chats.settings.messengerSettings') }"
          icon="tmi-settings-outline"
          :to="{ name: UserSettingsRoutes.Messenger }"
        />
      </tm-sidebar-header-wrapper>
    </template>
    <div class="chat">
      <div class="chat-container">
        <div
          class="chat-wrapper"
          :class="{
            'chat-wrapper--active': showMobileChat,
          }"
        >
          <resolver-with-component
            class="chat-resolver"
            :component-awaiter="() => import('@/components/views/chats/Chats.vue')"
            :component-awaiter-props="componentAwaiterProps"
          >
            <template v-slot:preloader>
              <chat-skeleton :chat-settings="chatSettings" />
            </template>
          </resolver-with-component>
        </div>
      </div>
    </div>
  </page-content>
  <router-view />
</template>

<script lang="ts">
import type { LocationQueryValue } from 'vue-router'
import {
  defineAsyncComponent,
  defineComponent,
  onMounted,
  computed,
  type PropType,
} from '@/composition/vue/compositionApi'
import { useChatUiState } from '@/composition/domain/chats/chatUiState'
import { useChatDetailsSidebarState } from '@/composition/domain/chats/chatRightSidebar'
import { getChatSettingsService, getGlobalStateService } from '@/core/container/ioc'
import PageContent from '@/components/layout/pageContent/PageContent.vue'
import { useBreakpoints } from '@/composition/breakpoints'
import { ChatSettingsRoutes } from '@/routes/types'
import TmSidebarHeaderWrapper from '@/components/layout/pageContent/sidebar/sidebarHeaderButtons/TmSidebarHeaderWrapper.vue'
import TmSidebarHeaderButton from '@/components/layout/pageContent/sidebar/sidebarHeaderButtons/TmSidebarHeaderButton.vue'
import ChatSkeleton from '@/components/domain/chats/content/ChatSkeleton.vue'
import ResolverWithComponent from '@/components/resolvers/ResolverWithComponent.vue'
import type Chats from '@/components/views/chats/Chats.vue'
import type { ComponentProps } from '@/composition/types'
import { UserSettingsRoutes } from '@/routes/user/settings'

export default defineComponent({
  components: {
    PageContent,
    TmSidebarHeaderWrapper,
    TmSidebarHeaderButton,
    ChatSkeleton,
    ChatsMenu: defineAsyncComponent(() => import('@/components/views/chats/ChatsMenu.vue')),
    ResolverWithComponent,
  },
  inheritAttrs: false,
  props: {
    dtStart: {
      type: [String, Array] as PropType<LocationQueryValue | LocationQueryValue[]>,
      required: false,
    },
    dtEnd: {
      type: [String, Array] as PropType<LocationQueryValue | LocationQueryValue[]>,
      required: false,
    },
    filter: {
      type: [String, Array] as PropType<LocationQueryValue | LocationQueryValue[]>,
      required: false,
    },
    id: {
      type: String,
      required: false,
    },
    phone: {
      type: [String, Array] as PropType<string | string[]>,
      required: false,
    },
    tags: {
      type: [String, Array] as PropType<LocationQueryValue | LocationQueryValue[]>,
      required: false,
    },
    contactTags: {
      type: [String, Array] as PropType<LocationQueryValue | LocationQueryValue[]>,
      required: false,
    },
  },
  setup(props) {
    const globalStateService = getGlobalStateService()
    const chatSettingsService = getChatSettingsService()
    const { showMobileChat, requestNotificationPermission, isShowChatFilterInDropdown } = useChatUiState()
    const { setState } = useChatDetailsSidebarState()
    const { isMobile, is3xlMax } = useBreakpoints()

    const chatSettings = computed(() => chatSettingsService.getChatSettings())

    const componentAwaiterProps = computed<ComponentProps<typeof Chats>>(() => {
      return {
        ...props,
        chatSettings: chatSettings.value,
      }
    })

    const sidebarWidth = computed(() => {
      if (isShowChatFilterInDropdown.value) {
        return 0
      }

      if (is3xlMax.value) {
        return 240
      }

      return 270
    })

    const initDetailsSidebar = () => {
      setState({
        sidebarOpen: globalStateService.getFieldValueSync('isChatDetailsSidebarOpen') !== false,
        visibleCustomFields: globalStateService.getFieldValueSync('isChatDetailsSidebarVisibleCustomFields') === true,
      })
    }

    onMounted(() => {
      requestNotificationPermission()
      initDetailsSidebar()
    })

    return {
      showMobileChat,
      sidebarWidth,
      isMobile,
      ChatSettingsRoutes,
      isShowChatFilterInDropdown,
      componentAwaiterProps,
      UserSettingsRoutes,
      chatSettings,
    }
  },
})
</script>
<style scoped lang="scss">
@import '@/styles/mixins.scss';

@media print {
  :global(.header),
  :global(.main-sidebar) {
    display: none !important;
  }
  :global(.chat),
  :global(.chat-content),
  :global(.chat-container),
  :global(.chat-content__main) {
    display: block !important;
    max-width: 100% !important;
    height: auto !important;
  }
}

:deep(.tm-scrollbar__content),
:deep(.page-content-main-block),
:deep(.page-content-default-wrapper__content) {
  height: 100%;
}
:deep(.page-content-main-wrap .page-content-main) {
  height: 100%;

  @include media-sm-x-max {
    padding: 0;
  }
  @include media-md-min {
    padding: 16px;
  }
  @include media-lg-min {
    padding: 16px;
  }
}
:deep(.tm-scrollbar--stable) {
  scrollbar-gutter: unset;
}
:deep(.tm-sidebar-header-wrapper) {
  margin-right: -12px;
}

.chat-resolver {
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  height: 100%;
  width: 100%;
}

.chat {
  height: 100%;
  width: 100%;
  background: $shade;

  &-container {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    max-width: 1788px;
    display: flex;
    flex-direction: column;

    @include media-sm-x-max {
      background: $white;
    }
  }

  &-wrapper {
    position: relative;
    background: $white;
    display: flex;
    width: 100%;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: hidden;

    @include media-md-min {
      border: 1px solid $border-color;
      border-radius: $border-radius-md;
      box-shadow: 0 0 16px rgba(0, 0, 0, 0.05);
    }

    @include media-sm-x-max {
      background: transparent;
      overflow: visible;
      height: 100%;
      transition: all $transition-300ms-ease-in-out;
    }

    :deep(.chat-sidebar) {
      position: relative;
      display: flex;
      flex-flow: column;
      flex: 1 0 100%;
      height: 100%;
      border-right: 1px solid $border-color;

      @include media-md-min {
        flex-shrink: 1;
      }

      .chat-sidebar-content {
        position: relative;
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
        flex-grow: 1;
        flex-shrink: 1;
      }

      @include media-sm-x-max {
        max-width: 100% !important;
        min-width: 100% !important;
      }
    }

    :deep(.chat-content) {
      display: flex;
      flex-flow: column;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      height: 100%;

      @include media-md-min {
        flex-shrink: 1;
      }
    }

    :deep(.chat-content-header) {
      display: flex;
      align-items: center;
      height: $contentHeaderHeight;
      border-bottom: 1px solid $gray2;
      padding: 0 12px 0 16px;
      flex-shrink: 0;
    }
  }
}

:deep(.chat-wrapper--active .chat-sidebar-resizable) {
  @include media-sm-x-max {
    display: none;
  }
}

:deep(.chat-sidebar-resizable) {
  @include media-sm-x-max {
    max-width: 100% !important;
    min-width: 100% !important;
  }
}

:deep(.chat-wrapper:not(.chat-wrapper--active) .chat-right-sidebar__backdrop) {
  @include media-sm-x-max {
    display: none;
  }
}
:deep(.chat-wrapper:not(.chat-wrapper--active) .tm-resizable-chat-right-sidebar) {
  @include media-sm-x-max {
    display: none;
  }
}
</style>
